<template>
    <AppModal ref="modal" :title="$t('labels.create_waybill')" size="full">
        <div class="modal-body">
            <div class="row">
                <FormGroup class="col-lg-3" :label="$t('labels.delivery_service')">
                    <DeliveryMethodSelect :model-value="method" disabled/>
                </FormGroup>
                <FormGroup class="col-lg-9" :label="$t('labels.address')">
                    <Input v-model="selectedAddress"/>
                </FormGroup>
                <FormGroup class="col-lg-3" :label="$t('labels.recipient_name')">
                    <Input v-model="selectedRecipientName"/>
                </FormGroup>
                <FormGroup class="col-lg-3" :label="$t('labels.recipient_phone')">
                    <PhoneInput v-model="selectedRecipientPhone"/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.city')">
                    <AppAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${method}/cities`"
                        :model-value="selectedCity?.uuid"
                        :selected-value="formatDeliveryCityName(selectedCity)"
                        @update:model-value="(newCity) => {selectedCity = newCity; selectedWarehouse = null}"
                        :query-params="{
                            orders: [
                                { column: 'name', dir: 'asc'}
                            ]
                        }"
                        :option-title-formatter="formatDeliveryCityName"
                        dropdownWidth="400px"
                        can-be-empty
                    >
                        <template #optionItem="{option}">
                            {{ formatDeliveryCityName(option) }}
                        </template>
                    </AppAutoComplete>
                </FormGroup>
                <FormGroup class="col-lg-4" :label="$t('labels.department')">
                    <AppAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${method}/cities/${selectedCity?.uuid}/warehouses`"
                        :model-value="selectedWarehouse?.uuid"
                        :selected-value="selectedWarehouse?.name"
                        :disabled="!selectedCity"
                        @update:model-value="selectedWarehouse = $event"
                        :query-params="{
                            orders: [
                                { column: 'name', dir: 'asc'}
                            ]
                        }"
                        can-be-empty
                    />
                </FormGroup>
            </div>
            <hr/>
            <div class="d-flex align-items-center mb-4">
                <h6 class="mb-0 font-weight-semibold">Створення ТТН</h6>
                <span class="badge badge-soft-warning ml-2">Перевірте дані перед створенням ТТН</span>
            </div>
            <div class="row">
                <FormGroup class="col-lg-4" :label="$t('labels.delivery_payer')">
                    <div class="btn-group btn-block">
                        <AppButton
                            v-for="option in payerTypes"
                            :key="option.key"
                            :color="payerType === option.key ? 'indigo' : 'light'"
                            @click="payerType = option.key"
                        >
                            {{ option.name }}
                        </AppButton>
                    </div>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.cargo_type')">
                    <AppSelect
                        @update:model-value="cargoType = $event"
                        :model-value="cargoType?.key"
                        :selected-value="cargoType?.name"
                        :options="cargoTypes"
                        option-key="key"
                        option-value="name"
                        can-be-empty
                    />
                </FormGroup>
                <FormGroup class="col-lg-1" :label="$t('labels.seats')">
                    <Input v-model="seatsAmount" type="number"/>
                </FormGroup>
                <FormGroup class="col-lg-1" :label="$t('labels.weight')">
                    <Input v-model="weight" type="number"/>
                </FormGroup>
                <FormGroup class="col-lg-4" :label="$t('labels.dimensions')">
                    <div class="d-flex align-items-center">
                        <Input v-model="length" type="number"/>
                        <span class="text-muted mx-2">×</span>
                        <Input v-model="width" type="number"/>
                        <span class="text-muted mx-2">×</span>
                        <Input v-model="height" type="number"/>
                    </div>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.cod')">
                    <AppButton
                        class="btn-block"
                        :color="codEnabled ? 'indigo' : 'light'"
                        @click="codEnabled = !codEnabled"
                    >
                        <i class="icon-coin-dollar mr-2"></i>
                        {{ codEnabled ? $t('labels.enabled') : $t('labels.disabled') }}
                    </AppButton>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.declared_value')">
                    <Input v-model="declaredValue" type="number"/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.cod_amount')" v-if="codEnabled">
                    <div class="d-flex align-items-center">
                        <span class="text-muted mr-2" v-if="isCodAmountEqualDeclaredValue">=</span>
                        <Input v-model="codAmount" type="number"/>
                    </div>
                </FormGroup>
                <FormGroup :class="isBackwardCodMode ? 'col-lg-3' : 'col-lg-6'" :label="$t('labels.cod_mode')" v-if="codEnabled">
                    <AppSelect
                        :model-value="codMode?.key"
                        :selected-value="codMode?.name"
                        :options="codModes"
                        option-key="key"
                        option-value="name"
                        disabled
                    />
                </FormGroup>
                <FormGroup class="col-lg-3" :label="$t('labels.backward_delivery_payer_type')" v-if="codEnabled && isBackwardCodMode">
                    <div class="btn-group btn-block">
                        <AppButton
                            v-for="option in backwardDeliveryPayerTypes"
                            :key="option.key"
                            :color="backwardDeliveryPayerType === option.key ? 'indigo' : 'light'"
                            @click="backwardDeliveryPayerType = option.key"
                        >
                            {{ option.name }}
                        </AppButton>
                    </div>
                </FormGroup>
            </div>
            <AppToast :title="senderTitle" class="mt-3">
                <template #headerElements>
                    <span class="text-muted text-truncate mr-2">{{ senderSummary }}</span>
                    <i
                        role="button"
                        :class="senderExpanded ? 'icon-arrow-up12' : 'icon-arrow-down12'"
                        @click="senderExpanded = !senderExpanded"
                    ></i>
                </template>
                <div class="toast-body pb-0" v-show="senderExpanded">
                    <div class="row">
                        <FormGroup class="col-lg-3" :label="$t('labels.counterparty')">
                            <AppSelect
                                @update:model-value="selectCredential($event)"
                                :model-value="credential?.uuid"
                                :selected-value="credential?.name"
                                :options="credentials"
                                option-key="uuid"
                                option-value="name"
                            />
                        </FormGroup>
                        <FormGroup class="col-lg-2" :label="$t('labels.contact_person')">
                            <Input :model-value="senderContactName" readonly/>
                        </FormGroup>
                        <FormGroup class="col-lg-2" :label="$t('labels.phone')">
                            <PhoneInput :model-value="senderPhone" readonly/>
                        </FormGroup>
                        <FormGroup class="col-lg-2" :label="$t('labels.city')">
                            <AppAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${method}/cities`"
                                :model-value="senderCity?.uuid"
                                :selected-value="formatDeliveryCityName(senderCity)"
                                @update:model-value="(newCity) => {senderCity = newCity; senderWarehouse = null}"
                                :query-params="{
                                    orders: [
                                        { column: 'name', dir: 'asc'}
                                    ]
                                }"
                                :option-title-formatter="formatDeliveryCityName"
                                can-be-empty
                            >
                                <template #optionItem="{option}">
                                    {{ formatDeliveryCityName(option) }}
                                </template>
                            </AppAutoComplete>
                        </FormGroup>
                        <FormGroup class="col-lg-3" :label="$t('labels.novapost_department')">
                            <AppAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${method}/cities/${senderCity?.uuid}/warehouses`"
                                :model-value="senderWarehouse?.uuid"
                                :selected-value="senderWarehouse?.name"
                                :disabled="!senderCity"
                                @update:model-value="senderWarehouse = $event"
                                :query-params="{
                                    orders: [
                                        { column: 'name', dir: 'asc'}
                                    ]
                                }"
                                can-be-empty
                            />
                        </FormGroup>
                    </div>
                </div>
            </AppToast>
            <AppToast :title="additionalTitle" class="mb-3">
                <template #headerElements>
                    <span class="text-muted text-truncate mr-2">{{ $t('labels.additional_summary') }}</span>
                    <i
                        role="button"
                        :class="additionalExpanded ? 'icon-arrow-up12' : 'icon-arrow-down12'"
                        @click="additionalExpanded = !additionalExpanded"
                    ></i>
                </template>
                <div class="toast-body pb-0" v-show="additionalExpanded">
                    <div class="row">
                        <FormGroup class="col-lg-4" :label="$t('labels.payment_method')">
                            <div class="btn-group btn-block">
                                <AppButton
                                    v-for="option in paymentMethods"
                                    :key="option.key"
                                    :color="paymentMethod === option.key ? 'indigo' : 'light'"
                                    @click="paymentMethod = option.key"
                                >
                                    {{ option.name }}
                                </AppButton>
                            </div>
                        </FormGroup>
                        <FormGroup class="col-lg-4" :label="$t('labels.sending_date')">
                            <DatePicker v-model="sendingDate"/>
                        </FormGroup>
                        <FormGroup class="col-lg-4" :label="$t('labels.internal_number')">
                            <Input v-model="internalNumber"/>
                        </FormGroup>
                        <FormGroup class="col-lg-6" :label="$t('labels.cargo_description')">
                            <Input v-model="cargoDescription"/>
                        </FormGroup>
                        <FormGroup class="col-lg-6" :label="$t('labels.additional_information')">
                            <Input v-model="additionalInformation"/>
                        </FormGroup>
                    </div>
                </div>
            </AppToast>
        </div>
        <div class="modal-footer d-flex justify-content-end">
            <AppButton color="light" class="mr-2" @click="hide()">
                {{ $t('buttons.dismiss') }}
            </AppButton>
            <AppButton color="indigo" :disabled="isCreating" @click="createWaybill()">
                <i :class="['mr-2', isCreating ? 'icon-spinner2 spinner' : 'icon-barcode2']"></i>
                {{ $t('buttons.create_waybill') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from "../../../../../../components/new/modal/AppModal.vue";
import AppButton from "../../../../../../components/new/button/AppButton.vue";
import FormGroup from "../../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../../components/new/form/Input.vue";
import PhoneInput from "../../../../../../components/new/form/PhoneInput.vue";
import AppAutoComplete from "../../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import AppSelect from "../../../../../../components/new/form/select/AppSelect.vue";
import AppToast from "../../../../../../components/new/AppToast.vue";
import DatePicker from "../../../../../../components/new/form/datePiker/DatePicker.vue";
import DeliveryMethodSelect from "../../../../../components/select/DeliveryMethodSelect.vue";

import { formatDeliveryCityName } from "../../../../../../utils";
import { httpClient } from "../../../../../../services/HttpClient";
import MessageNotificator from "../../../../../../services/MessageNotificator";

import { ApiCredentialsServiceEnum } from "../../../../../enums/ApiCredentialsServiceEnum";
import { NovapostCargoTypeEnum } from "../../../../../enums/NovapostCargoTypeEnum";
import { NovapostCodModeEnum } from "../../../../../enums/NovapostCodModeEnum";
import { NovapostPayerTypeEnum } from "../../../../../enums/NovapostPayerTypeEnum";
import { NovapostPaymentMethodEnum } from "../../../../../enums/NovapostPaymentMethodEnum";

export default {
    name: "CreateWaybillModal",
    components: {
        AppModal, AppButton, FormGroup, Input, PhoneInput, AppAutoComplete, AppSelect, AppToast, DatePicker, DeliveryMethodSelect
    },
    props: {
        method: {
            type: String,
            default: ''
        },
        address: {
            type: String,
            default: ''
        },
        recipientName: {
            type: String,
            default: ''
        },
        recipientPhone: {
            type: String,
            default: ''
        },
        orderSum: {
            type: Number,
            default: 0
        },
        externalOrderNumber: {
            type: String,
            default: ''
        },
        orderUuid: {
            type: String,
            required: true
        },
        deliveryUuid: {
            type: String,
            required: true
        }
    },
    emits: ['created'],
    data() {
        return {
            selectedAddress: '',
            selectedRecipientName: '',
            selectedRecipientPhone: '',
            selectedCity: null,
            selectedWarehouse: null,
            payerType: NovapostPayerTypeEnum.RECIPIENT,
            cargoType: null,
            seatsAmount: '',
            weight: '',
            length: '',
            width: '',
            height: '',
            senderExpanded: false,
            credential: null,
            credentials: [],
            senderContactName: '',
            senderPhone: '',
            senderCity: null,
            senderWarehouse: null,
            codEnabled: true,
            declaredValue: '',
            codAmount: '',
            backwardDeliveryPayerType: NovapostPayerTypeEnum.RECIPIENT,
            codMode: null,
            additionalExpanded: false,
            paymentMethod: NovapostPaymentMethodEnum.NON_CASH,
            cargoDescription: '',
            sendingDate: null,
            internalNumber: '',
            additionalInformation: '',
            isCreating: false
        };
    },
    computed: {
        payerTypes() {
            let payerTypes = Object.values(NovapostPayerTypeEnum).map(payerType => ({
                key: payerType,
                name: this.$t(`enums.NovapostPayerTypeEnum.${payerType}`)
            }));

            return payerTypes;
        },
        /**
         * Nova Poshta accepts only the sender or the recipient as the backward delivery payer.
         */
        backwardDeliveryPayerTypes() {
            let backwardDeliveryPayerTypes = this.payerTypes.filter(
                payerType => payerType.key !== NovapostPayerTypeEnum.THIRD_PERSON
            );

            return backwardDeliveryPayerTypes;
        },
        cargoTypes() {
            let cargoTypes = Object.values(NovapostCargoTypeEnum).map(cargoType => ({
                key: cargoType,
                name: this.$t(`enums.NovapostCargoTypeEnum.${cargoType}`)
            }));

            return cargoTypes;
        },
        senderTitle() {
            let title = `${this.$t('labels.sender')}<span class="badge badge-soft-success ml-2">${this.$t('labels.automatic')}</span>`;

            return title;
        },
        additionalTitle() {
            let title = `${this.$t('labels.additional')}<span class="badge badge-soft-secondary ml-2">${this.$t('labels.optional')}</span>`;

            return title;
        },
        paymentMethods() {
            let paymentMethods = Object.values(NovapostPaymentMethodEnum).map(paymentMethod => ({
                key: paymentMethod,
                name: this.$t(`enums.NovapostPaymentMethodEnum.${paymentMethod}`)
            }));

            return paymentMethods;
        },
        senderSummary() {
            let parts = [
                this.credential?.name,
                [this.senderCity?.name, this.senderWarehouse?.name].filter(Boolean).join(', '),
                this.formatPhone(this.senderPhone)
            ];

            let summary = parts.filter(Boolean).join(' · ');

            return summary;
        },
        isBackwardCodMode() {
            let isBackward = this.codMode?.key === NovapostCodModeEnum.BACKWARD;

            return isBackward;
        },
        isCodAmountEqualDeclaredValue() {
            let isEqual = Number(this.declaredValue) === Number(this.codAmount);

            return isEqual;
        },
        codModes() {
            let codModes = Object.values(NovapostCodModeEnum).map(codMode => ({
                key: codMode,
                name: this.$t(`enums.NovapostCodModeEnum.${codMode}`)
            }));

            return codModes;
        }
    },
    methods: {
        formatDeliveryCityName,
        show() {
            this.selectedAddress = this.address;
            this.selectedRecipientName = this.recipientName;
            this.selectedRecipientPhone = this.recipientPhone;
            this.selectedCity = null;
            this.selectedWarehouse = null;
            this.declaredValue = this.orderSum;
            this.codAmount = this.orderSum;
            this.internalNumber = this.externalOrderNumber;

            this.fetchCodEnabled();
            this.fetchCredentials();
            this.fetchDelivery();

            this.$refs.modal.show();
        },
        fetchDelivery() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.orderUuid}/deliveries/${this.deliveryUuid}`).then(response => {
                let delivery = response.data.data;

                this.selectedCity = delivery.city ?? null;
                this.selectedWarehouse = delivery.warehouse ?? null;
            });
        },
        fetchCredentials() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/core/api-credentials`, {
                service: ApiCredentialsServiceEnum.NOVAPOST,
                limit: 100
            }).then(response => {
                this.credentials = response.data.data.filter(
                    credential => credential.settings?.company_number_creating_enabled === true
                );

                this.selectCredential(this.credentials[0] ?? null);
            });
        },
        selectCredential(credential) {
            this.credential = credential;
            this.senderContactName = '';

            let defaultFields = credential?.settings?.default_fields ?? {};

            this.senderPhone = defaultFields.sender_phone ?? '';

            this.senderCity = defaultFields.sender_city_uuid
                ? {
                    uuid: defaultFields.sender_city_uuid,
                    name: defaultFields.sender_city_name ?? '',
                    district: defaultFields.sender_city_district ?? null,
                    region: defaultFields.sender_city_region ?? null
                }
                : null;

            this.senderWarehouse = defaultFields.sender_address_uuid
                ? {uuid: defaultFields.sender_address_uuid, name: defaultFields.sender_address_name ?? ''}
                : null;

            this.applyDefaultFields(defaultFields);
            this.fetchSenderContact();
        },
        applyDefaultFields(defaultFields) {
            let dimensions = defaultFields.dimensions ?? {};

            this.payerType = defaultFields.payer_type ?? NovapostPayerTypeEnum.RECIPIENT;
            this.paymentMethod = defaultFields.payment_method ?? NovapostPaymentMethodEnum.NON_CASH;
            this.cargoType = this.cargoTypes.find(cargoType => cargoType.key === defaultFields.cargo_type) ?? null;
            this.codMode = this.codModes.find(codMode => codMode.key === defaultFields.cod_mode) ?? null;
            this.seatsAmount = dimensions.seats_amount ?? '';
            this.weight = dimensions.weight ?? '';
            this.length = dimensions.length ?? '';
            this.width = dimensions.width ?? '';
            this.height = dimensions.height ?? '';
        },
        fetchSenderContact() {
            let defaultFields = this.credential?.settings?.default_fields ?? {};

            if (!this.credential?.token || !defaultFields.sender_id) {
                return;
            }

            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/delivery/providers/${ApiCredentialsServiceEnum.NOVAPOST}/${encodeURIComponent(this.credential.token)}/counterparties/${defaultFields.sender_id}/contact-persons`).then(response => {
                let contact = response.data.data.find(item => item.id === defaultFields.sender_contact_id) ?? null;

                this.senderContactName = contact?.name ?? '';
                this.senderPhone = defaultFields.sender_phone ?? contact?.phone ?? '';
            }).catch(() => {
                this.senderContactName = '';
            });
        },
        formatPhone(phone) {
            let digits = String(phone ?? '').replace(/\D/g, '');

            if (digits.length !== 12) {
                return phone ?? '';
            }

            let formatted = `+${digits.slice(0, 2)} (${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8, 10)}-${digits.slice(10)}`;

            return formatted;
        },
        fetchCodEnabled() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.orderUuid}/payments`).then(response => {
                let payment = response.data.data[0] ?? null;

                this.codEnabled = !(payment?.is_paid ?? false);
            });
        },
        hide() {
            this.$refs.modal.hide();
        },
        async createWaybill() {
            let defaultFields = this.credential?.settings?.default_fields ?? {};

            let request = {
                owner_api_credentials_uuid: this.credential?.uuid ?? null,
                recipient_name: this.selectedRecipientName || null,
                recipient_phone: this.selectedRecipientPhone || null,
                recipient_city_uuid: this.selectedCity?.uuid ?? null,
                recipient_address_uuid: this.selectedWarehouse?.uuid ?? null,
                description: this.cargoDescription || null,
                sender_id: defaultFields.sender_id ?? null,
                sender_contact_id: defaultFields.sender_contact_id ?? null,
                sender_phone: this.senderPhone || null,
                sender_city_uuid: this.senderCity?.uuid ?? null,
                sender_address_uuid: this.senderWarehouse?.uuid ?? null,
                payer_type: this.payerType,
                payment_method: this.paymentMethod,
                cargo_type: this.cargoType?.key ?? null,
                dimensions: {
                    weight: this.toNumber(this.weight),
                    length: this.toNumber(this.length),
                    width: this.toNumber(this.width),
                    height: this.toNumber(this.height),
                    seats_amount: this.toNumber(this.seatsAmount)
                },
                cod_enabled: this.codEnabled,
                declared_value: this.toNumber(this.declaredValue),
                cod_amount: this.codEnabled ? this.toNumber(this.codAmount) : null,
                cod_mode: this.codEnabled ? this.codMode?.key ?? null : null,
                backward_delivery_payer_type: this.codEnabled && this.isBackwardCodMode ? this.backwardDeliveryPayerType : null,
                internal_number: this.internalNumber || null,
                additional_information: this.additionalInformation || null,
                planned_shipment_date: this.sendingDate || null
            };

            this.isCreating = true;

            try {
                let response = await httpClient.post(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.orderUuid}/deliveries/${this.deliveryUuid}/create-ttn`, request);

                this.$emit('created', response.data.data);

                new MessageNotificator().success(this.$t('alerts.success'));

                this.hide();
            } finally {
                this.isCreating = false;
            }
        },
        toNumber(value) {
            let number = value === '' || value === null || value === undefined ? null : Number(value);

            return number;
        }
    }
}
</script>
